import {Action, ActionReducer, createReducer, on} from "@ngrx/store";
import {
  createSavedReportsFolderSuccess,
  deleteSavedReportsFolderSuccess,
  initFolderComponentObjects,
  initFolderComponentObjectsSuccess,
  loadSavedReportsFolders,
  loadSavedReportsFoldersFailure,
  loadSavedReportsFoldersSuccess,
  updateSavedReportsFolderSuccess
} from './savedReportsFolders.actions';
import {SavedReportsFoldersState} from "./savedReportsFolders";
import {Serializer, Serializers} from '../../interfaces/serializer';
import {SavedReportFolder} from "../../interfaces/saved-report-folder";
import {FolderComponentObject, FolderComponentObjects} from '../../classes/folder-component-object';
import {TypedAction} from '@ngrx/store/src/models';
import {Sort} from '../../libraries/sort';

const reducer: ActionReducer<SavedReportsFoldersState> = createReducer(
  {
    serialized: [],
    componentObjects: [],
    loading: false,
    loaded: false,
    initialized: false
  } as SavedReportsFoldersState,
  on(loadSavedReportsFolders, (state: SavedReportsFoldersState): SavedReportsFoldersState => {
    return {
      loading: true,
      serialized: [],
      componentObjects: [],
      loaded: false,
      initialized: false
    };
  }),
  on(loadSavedReportsFoldersSuccess, (state: SavedReportsFoldersState, action): SavedReportsFoldersState => {
    const savedReportFolders: Serializers<SavedReportFolder> = _sortSerializers(action.savedReportsFolders);

    return {
      ...state,
      loading: false,
      serialized: savedReportFolders,
      componentObjects: [
        new FolderComponentObject({
          id: null,
          type: '',
          attributes: {
            name: 'root',
            parent_folder_id: undefined,
            report_count: 0,
            folder_count: 0,
            templates_category_id: null
          },
          relationships: {}
        }),
        ...savedReportFolders.map((folder: Serializer<SavedReportFolder>): FolderComponentObject => new FolderComponentObject(folder))
      ],
      loaded: true,
      initialized: false
    };
  }),
  on(loadSavedReportsFoldersFailure, (state: SavedReportsFoldersState): SavedReportsFoldersState => {
    return { ...state, loading: false };
  }),
  on(createSavedReportsFolderSuccess, (state: SavedReportsFoldersState, action): SavedReportsFoldersState => {
    return {
      ...state,
      serialized: _sortSerializers([ ...state.serialized, action.newFolder ]),
      componentObjects: _sortObjects([ ...state.componentObjects, new FolderComponentObject(action.newFolder) ])
    };
  }),
  on(updateSavedReportsFolderSuccess, (state: SavedReportsFoldersState, action: TypedAction<string> & { savedReportFolder: Serializer<SavedReportFolder> }): SavedReportsFoldersState => {
    const componentObject: FolderComponentObject = state.componentObjects.find((folder: FolderComponentObject): boolean => folder.payload.id === action.savedReportFolder.id);

    componentObject.update(action.savedReportFolder);

    return {
      ...state,
      serialized: _sortSerializers(state.serialized.map((folder: Serializer<SavedReportFolder>): Serializer<SavedReportFolder> => folder.id === action.savedReportFolder.id && action.savedReportFolder || folder)),
      componentObjects: _sortObjects(state.componentObjects)
    };
  }),
  on(deleteSavedReportsFolderSuccess, (state: SavedReportsFoldersState, action): SavedReportsFoldersState => {
    return {
      ...state,
      serialized: state.serialized.filter((folder: Serializer<SavedReportFolder>): boolean => folder.id !== action.folderId),
      componentObjects: state.componentObjects.filter((folder: FolderComponentObject): boolean => folder.payload.id !== action.folderId)
    };
  }),
  on(initFolderComponentObjects, (state: SavedReportsFoldersState): SavedReportsFoldersState => {
    return {
      ...state,
      initialized: false
    }
  }),
  on(initFolderComponentObjectsSuccess, (state: SavedReportsFoldersState): SavedReportsFoldersState => {
    return {
      ...state,
      initialized: true
    };
  })
);

export const savedReportsFoldersReducer: (state: SavedReportsFoldersState, action: Action) => SavedReportsFoldersState= (state: SavedReportsFoldersState, action: Action) => {
  return reducer(state, action);
};

function _sortSerializers(folders: Serializers<SavedReportFolder>): Serializers<SavedReportFolder> {
  return folders.sort((a: Serializer<SavedReportFolder>, b: Serializer<SavedReportFolder>) => Sort.alphaAsc(a.attributes.name, b.attributes.name));
}

function _sortObjects(folders: FolderComponentObjects): FolderComponentObjects {
  return folders.sort((a: FolderComponentObject, b: FolderComponentObject) => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name));
}
